import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ListChecks, Search, AlertTriangle, CalendarClock, Pause, Inbox } from 'lucide-react';
import MyTaskRow from '../components/rd/MyTaskRow';
import { STATUS_META, GROUP_ORDER } from '../components/rd/rdTaskStatus';

const MANAGER_ROLES = ['Admin', 'ProductionSupervisor', 'InventoryManager'];
const EDIT_ROLES = ['Admin', 'Engineer'];

const Stat = ({ icon: Icon, label, value, tone, testId }) => (
  <div className={`flex items-center gap-3 rounded-lg border px-4 py-3 ${tone}`} data-testid={testId}>
    <Icon className="w-5 h-5 shrink-0" />
    <div>
      <p className="text-2xl font-semibold leading-none font-mono">{value}</p>
      <p className="text-[11px] uppercase tracking-wide mt-1 opacity-80">{label}</p>
    </div>
  </div>
);

const TaskGroup = ({ statusKey, tasks, canEdit, onStatus }) => {
  const meta = STATUS_META[statusKey];
  const Icon = meta.Icon;
  return (
    <Card className="overflow-hidden" data-testid={`my-tasks-group-${statusKey}`}>
      <div className={`flex items-center justify-between px-4 py-2 border-b ${meta.color}`}>
        <span className="text-sm font-semibold inline-flex items-center gap-1.5"><Icon className="w-4 h-4" />{meta.label}</span>
        <span className="text-xs font-mono" data-testid={`my-tasks-group-count-${statusKey}`}>{tasks.length}</span>
      </div>
      <div className="divide-y divide-slate-100">
        {tasks.map(t => <MyTaskRow key={t.id} task={t} canEdit={canEdit} onStatus={onStatus} />)}
      </div>
    </Card>
  );
};

const Filters = ({ q, setQ, project, setProject, projects, overdueOnly, setOverdueOnly, showDone, setShowDone }) => (
  <div className="flex items-center gap-4 flex-wrap">
    <div className="relative flex-1 min-w-[14rem] max-w-md">
      <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
      <Input className="pl-9" placeholder="Search tasks…" value={q} onChange={e => setQ(e.target.value)} data-testid="my-tasks-search" />
    </div>
    <Select value={project} onValueChange={setProject}>
      <SelectTrigger className="w-56" data-testid="my-tasks-project-filter"><SelectValue placeholder="All projects" /></SelectTrigger>
      <SelectContent>
        <SelectItem value="all">All projects</SelectItem>
        {projects.map(([id, name]) => <SelectItem key={id} value={id}>{name}</SelectItem>)}
      </SelectContent>
    </Select>
    <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
      <Switch checked={overdueOnly} onCheckedChange={setOverdueOnly} data-testid="my-tasks-overdue-toggle" /> Overdue only
    </label>
    <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
      <Switch checked={showDone} onCheckedChange={setShowDone} data-testid="my-tasks-show-done-toggle" /> Show done
    </label>
  </div>
);

export default function RDMyTasksPage() {
  const { api, user } = useAuth();
  const isManager = MANAGER_ROLES.includes(user?.role);
  const canEdit = EDIT_ROLES.includes(user?.role);
  const [searchParams] = useSearchParams();
  const paramUser = searchParams.get('user');
  const [viewUser, setViewUser] = useState(isManager && paramUser && paramUser !== user?.id ? paramUser : 'me');
  const [users, setUsers] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [project, setProject] = useState('all');
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [showDone, setShowDone] = useState(false);

  const load = useCallback(async () => {
    const params = { include_done: showDone };
    if (viewUser !== 'me') params.user_id = viewUser;
    try {
      const r = await api().get('/rd/my-tasks', { params });
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load tasks');
    } finally {
      setLoading(false);
    }
  }, [api, viewUser, showDone]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (isManager) api().get('/rd/assignable-users').then(r => setUsers(r.data || [])).catch(() => {});
  }, [api, isManager]);

  const onStatus = async (task, status) => {
    try {
      await api().put(`/rd/projects/${task.project_id}/tasks/${task.id}`, { status });
      toast.success(`Marked "${task.title}" as ${STATUS_META[status].label}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Update failed'); }
  };

  const tasks = useMemo(() => data?.tasks || [], [data]);
  const projects = useMemo(() => [...new Map(tasks.map(t => [t.project_id, t.project_name])).entries()], [tasks]);
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return tasks.filter(t =>
      (project === 'all' || t.project_id === project) &&
      (!overdueOnly || t.is_overdue) &&
      (!needle || `${t.title} ${t.description || ''} ${t.project_name}`.toLowerCase().includes(needle)));
  }, [tasks, q, project, overdueOnly]);
  const groups = GROUP_ORDER.map(k => [k, filtered.filter(t => t.status === k)]).filter(([, list]) => list.length);
  const isSelf = viewUser === 'me';
  const openCount = tasks.filter(t => t.status !== 'done').length;

  return (
    <div className="p-6 space-y-6" data-testid="rd-my-tasks-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2 text-slate-800" data-testid="my-tasks-heading">
            <ListChecks className="w-6 h-6 text-fuchsia-600" />
            {isSelf ? 'My Tasks' : `${data?.user?.name || 'Teammate'}'s Tasks`}
          </h1>
          <p className="text-sm text-slate-500 mt-1">Every open R&D task assigned to {isSelf ? 'you' : 'them'}, across all projects. Overdue tasks come first in each group.</p>
        </div>
        {isManager && (
          <Select value={viewUser} onValueChange={(v) => { setLoading(true); setProject('all'); setViewUser(v); }}>
            <SelectTrigger className="w-64" data-testid="my-tasks-user-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="me">Me ({user?.full_name || user?.email})</SelectItem>
              {users.filter(u => u.id !== user?.id).map(u => <SelectItem key={u.id} value={u.id}>{u.label}{u.role ? ` · ${u.role}` : ''}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat icon={ListChecks} label="Open" value={openCount} tone="bg-white border-slate-200 text-slate-700" testId="my-tasks-stat-open" />
        <Stat icon={CalendarClock} label="Overdue" value={data?.overdue || 0} tone="bg-rose-50 border-rose-200 text-rose-700" testId="my-tasks-stat-overdue" />
        <Stat icon={AlertTriangle} label="Blocked" value={data?.counts?.blocked || 0} tone="bg-rose-50/60 border-rose-100 text-rose-800" testId="my-tasks-stat-blocked" />
        <Stat icon={Pause} label="Delayed" value={data?.counts?.delayed || 0} tone="bg-amber-50 border-amber-200 text-amber-800" testId="my-tasks-stat-delayed" />
      </div>

      <Filters {...{ q, setQ, project, setProject, projects, overdueOnly, setOverdueOnly, showDone, setShowDone }} />

      {loading ? (
        <p className="text-sm text-slate-500" data-testid="my-tasks-loading">Loading tasks…</p>
      ) : groups.length === 0 ? (
        <Card className="p-10 text-center text-slate-500" data-testid="my-tasks-empty">
          <Inbox className="w-10 h-10 mx-auto mb-3 text-slate-300" />
          <p className="text-base">{tasks.length ? 'No tasks match these filters.' : 'Nothing assigned right now.'}</p>
          <p className="text-xs mt-1">Tasks show up here when someone assigns them in a project's Dependencies tab.</p>
        </Card>
      ) : (
        <div className="space-y-4">
          {groups.map(([k, list]) => <TaskGroup key={k} statusKey={k} tasks={list} canEdit={canEdit} onStatus={onStatus} />)}
        </div>
      )}
    </div>
  );
}
